import React, { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { 
  getVisiblePaths, 
  useMapPointSelect,
  useModeSwitching
} from '../Services';
import { cleanupVrpSession } from '../Services/UtilityServices/SessionService';
import { usePureMemoryPathManagement } from '../Services/PathServices/PureMemoryPathService';

// Create context
const VrpContext = createContext();

// Provide a hook to use the context
export const useVrp = () => {
  const context = useContext(VrpContext);
  if (!context) {
    throw new Error('useVrp must be used within a VrpProvider');
  }
  return context;
};

// Extract vehicle paths hook to manage the sidebar vehicle paths state
const useVehiclePathState = () => {
  // State for vehicle paths and selected vehicle
  const [vehiclePaths, setVehiclePaths] = useState([]);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(null);
  const [pathComplete, setPathComplete] = useState(false);
  
  return {
    vehiclePaths, 
    setVehiclePaths,
    selectedVehicleIndex, 
    setSelectedVehicleIndex,
    pathComplete,
    setPathComplete
  };
};

// Context provider component
export const VrpProvider = ({ children }) => {
  // Base state
  const [locations, setLocations] = useState([]);
  const [startId, setStartId] = useState('');
  const [showPanel, setShowPanel] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [maxCapacity, setMaxCapacity] = useState(0);

  // Use vehicle path state
  const {
    vehiclePaths,
    setVehiclePaths,
    selectedVehicleIndex,
    setSelectedVehicleIndex,
    pathComplete,
    setPathComplete,
  } = useVehiclePathState();

  // Use existing service hooks
  const { 
    currentMode, 
    setCurrentMode,
    currentModeRef,
    setupSubmode, 
    handleSubmodeSwitching
  } = useModeSwitching();

  // Use navigation management
  const isMountedRef = useRef(true);
  const isNavigatingRef = useRef(false);
  
  const goToHome = useCallback(() => {
    if (isMountedRef.current) {
      isNavigatingRef.current = true;
      window.location.href = '/';
    }
  }, []);

  // Use pure memory path management hook (like TSP)
  const {
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    autoVehicleCount,
    setAutoVehicleCount,
    customVehicleCount,
    setCustomVehicleCount,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath,
    saveAutoPath,
    saveCustomPath,
    clearAllPaths: clearMemoryPaths,
    clearAutoPath,
    clearCustomPath
  } = usePureMemoryPathManagement(locations);

  // Use map point selection hook with enhanced return values
  const { 
    mapPointSelectCallback, 
    setMapPointSelect,
    clearMapPointSelect 
  } = useMapPointSelect();

  // Map reference for jump-to-location functionality
  const mapRef = useRef(null);

  // Function to jump to a specific location on the map
  const jumpToLocation = useCallback((latitude, longitude, zoom = 10) => {
    if (!mapRef.current || !mapRef.current.loaded()) {
      console.warn('Map is not ready for jumping to location');
      return;
    }

    try {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        duration: 1000, // Animation duration in milliseconds
        essential: true // This animation is considered essential with respect to prefers-reduced-motion
      });
    } catch (error) {
      console.error('Error jumping to location:', error);
    }
  }, []);

  // Function to set map reference (to be called from MapComponent)
  const setMapRef = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Callback to clear all paths (pure memory - like TSP)
  const clearAllPaths = useCallback(async () => {
    try {
      // Clear paths from memory only
      clearMemoryPaths();
      setPathComplete(false);
      
      // Optional: clean up any backend storage
      await cleanupVrpSession();
      
      console.log('[VRP-CONTEXT] Cleared all paths from memory (like TSP)');
    } catch (err) {
      console.error('Failed to clear paths:', err);
    }
  }, [clearMemoryPaths, setPathComplete]);

  // Initialize session data - Pure memory approach (like TSP)
  const initializeSession = useCallback(async () => {
    try {
      // Clean up any existing session data
      await cleanupVrpSession();
      
      // Initialize with empty state - pure memory approach
      setIsLoading(true);
      setAutoPath([]);
      setCustomPath([]);
      setPathComplete(false);
      
      console.log('[VRP-CONTEXT] Initialized with pure memory state (like TSP)');
    } catch (error) {
      console.error('Failed to initialize session:', error);
      // If cleanup fails, still initialize with empty state
      setAutoPath([]);
      setCustomPath([]);
      setPathComplete(false);
    } finally {
      setIsLoading(false);
    }
  }, [setAutoPath, setCustomPath, setPathComplete]);

  // Calculate visible paths for current mode
  const visiblePaths = useMemo(() => {
    const paths = getVisiblePaths(currentMode, autoPath, customPath);

    // Handle different custom path formats
    if (customPath) {
      if (customPath.vehiclePaths && Array.isArray(customPath.vehiclePaths) && 
          (currentMode === 'Custom Path' || currentMode === 'Comparison Mode')) {
        // Use vehiclePaths format
        // In comparison mode, ensure customPath data is correctly passed
        if (currentMode === 'Comparison Mode') {
          paths.custom = {...customPath};
          paths.customVehiclePaths = customPath.vehiclePaths;
          
          // Ensure correct vehicle count, prioritize value from customPath
          if (customPath.customVehicleCount) {
            paths.customVehicleCount = customPath.customVehicleCount;
          } else if (customPath.vehiclePaths.length > 0) {
            paths.customVehicleCount = customPath.vehiclePaths.length;
          } else {
            paths.customVehicleCount = customVehicleCount;
          }
        } else {
          paths.vehiclePaths = customPath.vehiclePaths;
        }
      } else if (customPath.type === 'FeatureCollection' && currentMode === 'Comparison Mode') {
        // If in GeoJSON format, pass complete GeoJSON object in comparison mode

        paths.custom = customPath;
        paths.customVehicleCount = customVehicleCount;  // Add vehicle count information
        
        // Try to extract vehicle path data from GeoJSON
        if (customPath.features && Array.isArray(customPath.features)) {
          paths.customVehiclePaths = customPath.features.map((feature, index) => ({
            id: feature.properties?.vehicle_id || index + 1,
            vehicle_id: feature.properties?.vehicle_id || index + 1,
            color: feature.properties?.color || '#FF5722',
            distance: feature.properties?.distance || 0,
            path: feature.properties?.path_ids || []
          }));
        }
      } else if (Array.isArray(customPath) && currentMode === 'Comparison Mode') {
        // When customPath is a simple array, also add vehicle count info
        paths.customVehicleCount = customVehicleCount;
      }
    }
    
    return paths;
  }, [currentMode, autoPath, customPath, customVehicleCount]);  // Add dependencies

  // Initialize component on mount
  const initialize = useCallback(() => {
    isMountedRef.current = true;
    isNavigatingRef.current = false;
    initializeSession();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [initializeSession]);

  // Value provided to context
  const contextValue = {
    // Base state
    locations,
    setLocations,
    startId,
    setStartId,
    showPanel,
    setShowPanel,
    isLoading,
    maxCapacity,
    setMaxCapacity,
    
    // Navigation
    goToHome,
    isMountedRef,
    isNavigatingRef,
    initialize,
    
    // Mode management
    currentMode,
    setCurrentMode,
    currentModeRef,
    setupSubmode,
    handleSubmodeSwitching,
    
    // Path management
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath: (path, metadata) => setPath(path, currentMode, metadata),
    saveAutoPath,
    saveCustomPath,
    clearAllPaths,
    visiblePaths,
    
    // Vehicle path management - for unfinished path visualization
    vehiclePaths,
    setVehiclePaths,
    selectedVehicleIndex,
    setSelectedVehicleIndex,
    pathComplete,
    setPathComplete,
    customVehicleCount,
    setCustomVehicleCount,
    autoVehicleCount,
    setAutoVehicleCount,
    
    // Map interaction
    mapPointSelectCallback,
    setMapPointSelect,
    clearMapPointSelect,
    jumpToLocation,
    setMapRef
  };

  return (
    <VrpContext.Provider value={contextValue}>
      {children}
    </VrpContext.Provider>
  );
}; 